import React, { useEffect, useMemo, useState, Component } from 'react';
import { X, Banknote, CreditCard, Smartphone, Loader2, Receipt, CheckSquare, Square, AlertCircle, CheckCircle2, Mail, Printer, ArrowLeft, FileText } from 'lucide-react';
import { Button } from '../ui/Button';
import { cn, formatCurrency } from '../../lib/utils';
import { useTableOrderItems } from '../../lib/table-hooks';
import { useBillingInfo, processPayment, openPrintableInvoice, PaymentItemSnapshot, PaymentRecord, BillingInfo } from '../../lib/billing-hooks';
import { TableWithOrder } from '../../lib/types';
interface PayTableModalProps {
  isOpen: boolean;
  onClose: () => void;
  table: TableWithOrder | null;
  restaurantId: number | string | undefined;
  onSuccess: (allPaid: boolean) => void;
  createdBy?: string | null;
}
type PayMethod = 'cash' | 'sinpe' | 'card';
type Step = 'select' | 'paid' | 'invoice';
export function PayTableModal({
  isOpen,
  onClose,
  table,
  restaurantId,
  onSuccess,
  createdBy
}: PayTableModalProps) {
  const orderId = table?.current_order?.id;
  const {
    items,
    loading: itemsLoading,
    refetch
  } = useTableOrderItems(table?.id, restaurantId ? String(restaurantId) : undefined);
  const {
    info: billingInfo,
    loading: infoLoading
  } = useBillingInfo(restaurantId);
  const [step, setStep] = useState<Step>('select');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [method, setMethod] = useState<PayMethod>('cash');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [completedPayment, setCompletedPayment] = useState<PaymentRecord | null>(null);
  const [allPaid, setAllPaid] = useState(false);
  // Customer billing data (only collected when generating invoice)
  const [customerName, setCustomerName] = useState('');
  const [customerLegalId, setCustomerLegalId] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const unpaidItems = useMemo(() => items.filter((i: any) => !i.paid_at), [items]);
  useEffect(() => {
    if (isOpen && unpaidItems.length > 0 && step === 'select') {
      setSelectedIds(new Set(unpaidItems.map(i => i.order_item_id)));
    }
    if (!isOpen) {
      setStep('select');
      setSelectedIds(new Set());
      setError(null);
      setMethod('cash');
      setCompletedPayment(null);
      setAllPaid(false);
      setCustomerName('');
      setCustomerLegalId('');
      setCustomerEmail('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, unpaidItems.length]);
  const toggleItem = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);else next.add(id);
      return next;
    });
  };
  const allSelected = unpaidItems.length > 0 && selectedIds.size === unpaidItems.length;
  const toggleAll = () => {
    if (allSelected) setSelectedIds(new Set());else setSelectedIds(new Set(unpaidItems.map(i => i.order_item_id)));
  };
  const selectedItems = unpaidItems.filter(i => selectedIds.has(i.order_item_id));
  const total = selectedItems.reduce((s, i) => s + (i.line_total || 0), 0);
  const isPartial = selectedItems.length < unpaidItems.length;
  const handleSubmit = async () => {
    if (!orderId || !restaurantId) {
      setError('Falta información de la mesa.');
      return;
    }
    if (selectedItems.length === 0) {
      setError('Selecciona al menos un ítem para cobrar.');
      return;
    }
    setError(null);
    setSubmitting(true);
    const snapshot: PaymentItemSnapshot[] = selectedItems.map(i => ({
      order_item_id: i.order_item_id,
      item_name: i.item_name,
      quantity: i.quantity,
      unit_price: i.unit_price,
      line_total: i.line_total,
      notes: i.notes || null
    }));
    const result = await processPayment({
      restaurantId,
      orderId,
      tableId: table?.id || null,
      amount: total,
      method,
      items: snapshot,
      isPartial,
      createdBy: createdBy || null
    });
    setSubmitting(false);
    if (!result.success || !result.payment) {
      setError(result.error || 'Error al procesar el pago.');
      return;
    }
    await refetch();
    const allPaidNow = !isPartial || selectedItems.length === unpaidItems.length;
    setCompletedPayment(result.payment);
    setAllPaid(allPaidNow);
    setStep('paid');
    onSuccess(allPaidNow);
  };
  if (!isOpen || !table) return null;
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-id="element-1017">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] flex flex-col shadow-2xl" data-id="element-1018">
        {step === 'select' && <SelectStep table={table} billingInfo={billingInfo} infoLoading={infoLoading} itemsLoading={itemsLoading} unpaidItems={unpaidItems} selectedIds={selectedIds} allSelected={allSelected} toggleAll={toggleAll} toggleItem={toggleItem} method={method} setMethod={setMethod} error={error} isPartial={isPartial} total={total} selectedItems={selectedItems} submitting={submitting} onClose={onClose} onSubmit={handleSubmit} data-id="element-1019" />}

        {step === 'paid' && completedPayment && <PaidStep payment={completedPayment} allPaid={allPaid} onClose={onClose} onGoInvoice={() => setStep('invoice')} onChargeRemaining={() => {
        setStep('select');
        setCompletedPayment(null);
        setSelectedIds(new Set());
      }} data-id="element-1020" />}

        {step === 'invoice' && completedPayment && billingInfo && <InvoiceStep table={table} billingInfo={billingInfo} payment={completedPayment} customerName={customerName} setCustomerName={setCustomerName} customerLegalId={customerLegalId} setCustomerLegalId={setCustomerLegalId} customerEmail={customerEmail} setCustomerEmail={setCustomerEmail} onBack={() => setStep('paid')} onClose={onClose} data-id="element-1021" />}
      </div>
    </div>;
}
// ─── STEP 1: Select items + payment method ────────────────────────────────
function SelectStep(props: {
  table: TableWithOrder;
  billingInfo: BillingInfo | null;
  infoLoading: boolean;
  itemsLoading: boolean;
  unpaidItems: any[];
  selectedIds: Set<string>;
  allSelected: boolean;
  toggleAll: () => void;
  toggleItem: (id: string) => void;
  method: 'cash' | 'sinpe' | 'card';
  setMethod: (m: 'cash' | 'sinpe' | 'card') => void;
  error: string | null;
  isPartial: boolean;
  total: number;
  selectedItems: any[];
  submitting: boolean;
  onClose: () => void;
  onSubmit: () => void;
}) {
  const {
    table,
    itemsLoading,
    unpaidItems,
    selectedIds,
    allSelected,
    toggleAll,
    toggleItem,
    method,
    setMethod,
    error,
    isPartial,
    total,
    selectedItems,
    submitting,
    onClose,
    onSubmit
  } = props;
  // ─── Cash received → change calculator (only shown when method = cash) ───
  const [cashReceived, setCashReceived] = useState<string>('');
  // Reset cash field whenever method changes or total changes meaningfully
  useEffect(() => {
    if (method !== 'cash') setCashReceived('');
  }, [method]);
  const cashReceivedNum = parseFloat(cashReceived.replace(/[^0-9.]/g, '')) || 0;
  const change = cashReceivedNum - total;
  const hasEnoughCash = cashReceivedNum >= total && cashReceivedNum > 0;
  const quickCashAmounts = useMemo(() => {
    if (total <= 0) return [] as number[];
    // Round up to nearest 1 000, 5 000, 10 000, 20 000 — only keep those >= total
    const candidates = [Math.ceil(total / 1000) * 1000, Math.ceil(total / 5000) * 5000, Math.ceil(total / 10000) * 10000, Math.ceil(total / 20000) * 20000];
    return Array.from(new Set(candidates)).filter(v => v >= total).slice(0, 4);
  }, [total]);
  return <>
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0" data-id="element-1022">
        <div data-id="element-1023">
          <h2 className="text-xl font-black text-gray-900 flex items-center gap-2" data-id="element-1024">
            <Receipt className="w-5 h-5 text-amber-500" data-id="element-1025" />
            Cobrar {table.table_name || `Mesa ${table.table_number}`}
          </h2>
          <p className="text-xs text-gray-500 mt-0.5" data-id="element-1026">
            Selecciona los ítems a cobrar (división de cuenta disponible)
          </p>
        </div>
        <button onClick={onClose} disabled={submitting} className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-40" data-id="element-1027">
          <X className="w-5 h-5 text-gray-500" data-id="element-1028" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4" data-id="element-1029">
        {itemsLoading ? <div className="flex justify-center py-12" data-id="element-1030">
            <Loader2 className="w-6 h-6 text-gray-400 animate-spin" data-id="element-1031" />
          </div> : unpaidItems.length === 0 ? <div className="text-center py-12 text-gray-500" data-id="element-1032">
            <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-400 mb-3" data-id="element-1033" />
            <p className="font-bold" data-id="element-1034">No hay ítems pendientes de cobro</p>
            <p className="text-sm" data-id="element-1035">Esta mesa ya fue cobrada por completo.</p>
          </div> : <>
            <button onClick={toggleAll} className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors" data-id="element-1036">
              <span className="flex items-center gap-2 text-sm font-bold text-gray-900" data-id="element-1037">
                {allSelected ? <CheckSquare className="w-4 h-4 text-amber-500" data-id="element-1038" /> : <Square className="w-4 h-4 text-gray-400" data-id="element-1039" />}
                {allSelected ? 'Deseleccionar todo' : 'Seleccionar todo'}
              </span>
              <span className="text-xs font-medium text-gray-500" data-id="element-1040">
                {selectedIds.size} / {unpaidItems.length} ítems
              </span>
            </button>

            <div className="space-y-1.5" data-id="element-1041">
              {unpaidItems.map(item => {
            const isSelected = selectedIds.has(item.order_item_id);
            return <button key={item.order_item_id} onClick={() => toggleItem(item.order_item_id)} className={cn('w-full text-left flex items-start gap-3 px-3 py-3 rounded-xl border-2 transition-all', isSelected ? 'border-amber-400 bg-amber-50/50' : 'border-gray-100 hover:border-gray-200 bg-white')} data-id="element-1042">
                    <div className="mt-0.5 shrink-0" data-id="element-1043">
                      {isSelected ? <CheckSquare className="w-5 h-5 text-amber-500" data-id="element-1044" /> : <Square className="w-5 h-5 text-gray-300" data-id="element-1045" />}
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-1046">
                      <div className="flex items-baseline gap-2" data-id="element-1047">
                        <span className="font-black text-gray-900 bg-gray-100 px-1.5 rounded text-xs" data-id="element-1048">
                          {item.quantity}×
                        </span>
                        <p className="font-bold text-gray-900 text-sm truncate" data-id="element-1049">
                          {item.item_name}
                        </p>
                      </div>
                      {item.notes && <p className="text-xs text-gray-500 italic mt-0.5" data-id="element-1050">
                          {item.notes}
                        </p>}
                    </div>
                    <span className="font-bold text-gray-900 text-sm tabular-nums shrink-0" data-id="element-1051">
                      {formatCurrency(item.line_total)}
                    </span>
                  </button>;
          })}
            </div>

            <div className="pt-2" data-id="element-1052">
              <p className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-2" data-id="element-1053">
                Método de pago
              </p>
              <div className="grid grid-cols-3 gap-2" data-id="element-1054">
                {([{
              id: 'cash',
              label: 'Efectivo',
              Icon: Banknote
            }, {
              id: 'sinpe',
              label: 'SINPE',
              Icon: Smartphone
            }, {
              id: 'card',
              label: 'Tarjeta',
              Icon: CreditCard
            }] as const).map(({
              id,
              label,
              Icon
            }) => <button key={id} onClick={() => setMethod(id)} className={cn('flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all', method === id ? 'border-amber-400 bg-amber-50 text-amber-700' : 'border-gray-100 hover:border-gray-200 text-gray-600')} data-id="element-1055">
                    <Icon className="w-5 h-5 mb-1" data-id="element-1056" />
                    <span className="text-xs font-bold" data-id="element-1057">{label}</span>
                  </button>)}
              </div>
            </div>

            {/* ─── Cash received + change (only for cash method) ─── */}
            {method === 'cash' && selectedItems.length > 0 && <div className="rounded-2xl border-2 border-amber-200 bg-amber-50/40 p-4 space-y-3" data-id="element-1058">
                <div className="flex items-center justify-between" data-id="element-1059">
                  <p className="text-xs font-black text-amber-700 uppercase tracking-wider" data-id="element-1060">
                    💵 Paga con (efectivo)
                  </p>
                  <p className="text-[10px] text-amber-600 font-medium" data-id="element-1061">
                    opcional — calcula el vuelto
                  </p>
                </div>
                <div className="relative" data-id="element-1062">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl font-black text-amber-500 pointer-events-none" data-id="element-1063">
                    ₡
                  </span>
                  <input type="text" inputMode="decimal" value={cashReceived} onChange={e => setCashReceived(e.target.value.replace(/[^0-9.]/g, ''))} placeholder="0" className="w-full pl-10 pr-4 py-3 rounded-xl border-2 border-amber-200 focus:border-amber-500 focus:outline-none text-2xl font-black text-gray-900 placeholder:text-gray-300 bg-white tabular-nums" data-id="element-1064" />
                </div>
                {quickCashAmounts.length > 0 && <div className="flex flex-wrap gap-2" data-id="element-1065">
                    {quickCashAmounts.map(amt => <button key={amt} type="button" onClick={() => setCashReceived(String(amt))} className={cn('px-3 py-1.5 rounded-lg text-xs font-bold border-2 transition-colors tabular-nums', cashReceivedNum === amt ? 'border-amber-500 bg-amber-500 text-white' : 'border-amber-200 bg-white text-amber-700 hover:border-amber-400')} data-id="element-1066">
                        {formatCurrency(amt)}
                      </button>)}
                  </div>}
                {cashReceivedNum > 0 && <div className={cn('rounded-xl px-4 py-3 flex items-baseline justify-between', hasEnoughCash ? 'bg-emerald-500 text-white' : 'bg-red-50 border-2 border-red-200')} data-id="element-1067">
                    <span className={cn('text-xs font-black uppercase tracking-wider', hasEnoughCash ? 'text-white/90' : 'text-red-600')} data-id="element-1068">
                      {hasEnoughCash ? 'Vuelto a entregar' : 'Falta'}
                    </span>
                    <span className={cn('text-2xl font-black tabular-nums', hasEnoughCash ? 'text-white' : 'text-red-700')} data-id="element-1069">
                      {formatCurrency(hasEnoughCash ? change : Math.abs(change))}
                    </span>
                  </div>}
              </div>}

            {error && <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700 flex items-start gap-2" data-id="element-1070">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" data-id="element-1071" />
                {error}
              </div>}
          </>}
      </div>

      {unpaidItems.length > 0 && <div className="px-6 py-4 border-t border-gray-100 shrink-0 bg-gray-50 rounded-b-2xl" data-id="element-1072">
          <div className="flex justify-between items-baseline mb-3" data-id="element-1073">
            <div data-id="element-1074">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-1075">
                {isPartial ? 'Total parcial' : 'Total a cobrar'}
              </p>
              {isPartial && <p className="text-[10px] text-amber-600 font-bold" data-id="element-1076">
                  Cuenta dividida — quedará saldo pendiente
                </p>}
            </div>
            <span className="text-3xl font-black text-amber-500 tabular-nums" data-id="element-1077">
              {formatCurrency(total)}
            </span>
          </div>
          <Button fullWidth size="lg" onClick={onSubmit} disabled={submitting || selectedItems.length === 0} className="bg-gray-900 hover:bg-gray-800 text-white font-bold shadow-lg" data-id="element-1078">
            {submitting ? <span className="flex items-center justify-center gap-2" data-id="element-1079">
                <Loader2 className="w-5 h-5 animate-spin" data-id="element-1080" />
                Procesando...
              </span> : <span className="flex items-center justify-center gap-2" data-id="element-1081">
                <Banknote className="w-5 h-5" data-id="element-1082" />
                Cobrar
              </span>}
          </Button>
        </div>}
    </>;
}
// ─── STEP 2: Payment confirmed — choose: invoice or close ─────────────────
function PaidStep(props: {
  payment: PaymentRecord;
  allPaid: boolean;
  onClose: () => void;
  onGoInvoice: () => void;
  onChargeRemaining: () => void;
}) {
  const {
    payment,
    allPaid,
    onClose,
    onGoInvoice,
    onChargeRemaining
  } = props;
  const methodLabel = payment.method === 'cash' ? 'Efectivo' : payment.method === 'sinpe' ? 'SINPE Móvil' : 'Tarjeta';
  return <>
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0" data-id="element-1083">
        <div className="flex items-center gap-3" data-id="element-1084">
          <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center" data-id="element-1085">
            <CheckCircle2 className="w-6 h-6 text-emerald-500" data-id="element-1086" />
          </div>
          <div data-id="element-1087">
            <h2 className="text-xl font-black text-gray-900" data-id="element-1088">
              Pago registrado
            </h2>
            <p className="text-xs text-gray-500" data-id="element-1089">
              {methodLabel} · {formatCurrency(payment.amount)}
              {!allPaid && ' · Saldo pendiente en mesa'}
            </p>
          </div>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1090">
          <X className="w-5 h-5 text-gray-500" data-id="element-1091" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-8" data-id="element-1092">
        <div className="text-center mb-6" data-id="element-1093">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-50 mb-4" data-id="element-1094">
            <CheckCircle2 className="w-12 h-12 text-emerald-500" data-id="element-1095" />
          </div>
          <h3 className="text-2xl font-black text-gray-900 mb-1" data-id="element-1096">
            ¡Cobro exitoso!
          </h3>
          <p className="text-sm text-gray-500" data-id="element-1097">¿El cliente desea factura?</p>
        </div>

        <div className="max-w-sm mx-auto space-y-2" data-id="element-1098">
          <button onClick={onGoInvoice} className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-gray-900 hover:bg-gray-800 text-white font-bold transition-colors" data-id="element-1099">
            <FileText className="w-5 h-5" data-id="element-1100" />
            Sí, generar factura
          </button>
          {!allPaid ? <button onClick={onChargeRemaining} className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-bold transition-colors" data-id="element-1101">
              <ArrowLeft className="w-4 h-4" data-id="element-1102" />
              Cobrar saldo restante
            </button> : <button onClick={onClose} className="w-full py-3 rounded-xl border-2 border-gray-200 hover:border-gray-300 text-gray-700 font-bold transition-colors" data-id="element-1103">
              No, cerrar
            </button>}
        </div>
      </div>
    </>;
}
// ─── STEP 3: Generate invoice — collect customer data + send by email ────
function InvoiceStep(props: {
  table: TableWithOrder;
  billingInfo: BillingInfo;
  payment: PaymentRecord;
  customerName: string;
  setCustomerName: (s: string) => void;
  customerLegalId: string;
  setCustomerLegalId: (s: string) => void;
  customerEmail: string;
  setCustomerEmail: (s: string) => void;
  onBack: () => void;
  onClose: () => void;
}) {
  const {
    table,
    billingInfo,
    payment,
    customerName,
    setCustomerName,
    customerLegalId,
    setCustomerLegalId,
    customerEmail,
    setCustomerEmail,
    onBack,
    onClose
  } = props;
  const consecStr = String(payment.consecutive_number).padStart(6, '0');
  const invoiceUrl = `${window.location.origin}/factura/${payment.id}`;
  const buildEmailBody = () => {
    const greeting = customerName.trim() || 'Cliente';
    return `Estimad@ ${greeting},\n\n` + `Agradecemos profundamente su visita. Esperamos que su paso por nuestras instalaciones haya sido de su agrado.\n\n` + (customerLegalId.trim() ? `Datos del cliente:\n` + `Nombre: ${greeting}\n` + `Cédula / Identificación: ${customerLegalId.trim()}\n\n` : '') + `Adjunto podrá encontrar la factura solicitada:\n` + `${invoiceUrl}\n\n` + `Factura N° ${consecStr} · Total: ${new Intl.NumberFormat('es-CR', {
      style: 'currency',
      currency: 'CRC',
      minimumFractionDigits: 0
    }).format(payment.amount)}\n\n` + `¡Lo esperamos pronto!\n\n` + `Att: ${billingInfo.business_name}`;
  };
  const handleSendEmail = () => {
    if (!customerEmail.trim()) {
      alert('Ingresa el correo electrónico del cliente.');
      return;
    }
    const subject = `Factura ${consecStr} — ${billingInfo.business_name}`;
    const url = `mailto:${encodeURIComponent(customerEmail)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(buildEmailBody())}`;
    window.location.href = url;
  };
  const handlePrint = () => {
    openPrintableInvoice({
      info: billingInfo,
      payment,
      tableLabel: table.table_name || `Mesa ${table.table_number}`
    });
  };
  const canSend = customerName.trim() && customerEmail.trim();
  return <>
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0" data-id="element-1104">
        <div className="flex items-center gap-3" data-id="element-1105">
          <button onClick={onBack} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1106">
            <ArrowLeft className="w-4 h-4 text-gray-600" data-id="element-1107" />
          </button>
          <div data-id="element-1108">
            <h2 className="text-xl font-black text-gray-900 flex items-center gap-2" data-id="element-1109">
              <FileText className="w-5 h-5 text-gray-700" data-id="element-1110" />
              Generar factura
            </h2>
            <p className="text-xs text-gray-500" data-id="element-1111">
              Factura N° {consecStr} · {formatCurrency(payment.amount)}
            </p>
          </div>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1112">
          <X className="w-5 h-5 text-gray-500" data-id="element-1113" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4" data-id="element-1114">
        <p className="text-xs text-gray-500 leading-relaxed bg-gray-50 border border-gray-100 rounded-xl px-4 py-3" data-id="element-1115">
          Ingresa los datos del cliente para emitir la factura. Se enviará por
          correo electrónico con un enlace al comprobante.
        </p>

        <div data-id="element-1116">
          <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5" data-id="element-1117">
            Nombre completo *
          </label>
          <input type="text" value={customerName} onChange={e => setCustomerName(e.target.value)} placeholder="Ej. María Pérez Rojas" className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-100 focus:border-amber-400 focus:outline-none text-sm font-medium text-gray-900 placeholder:text-gray-400 bg-white" data-id="element-1118" />
        </div>

        <div data-id="element-1119">
          <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5" data-id="element-1120">
            Cédula / Identificación
            <span className="text-gray-400 font-medium normal-case ml-1" data-id="element-1121">
              (opcional)
            </span>
          </label>
          <input type="text" value={customerLegalId} onChange={e => setCustomerLegalId(e.target.value)} placeholder="Ej. 1-1234-5678" className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-100 focus:border-amber-400 focus:outline-none text-sm font-medium text-gray-900 placeholder:text-gray-400 bg-white" data-id="element-1122" />
        </div>

        <div data-id="element-1123">
          <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5" data-id="element-1124">
            Correo electrónico *
          </label>
          <input type="email" value={customerEmail} onChange={e => setCustomerEmail(e.target.value)} placeholder="cliente@correo.com" className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-100 focus:border-amber-400 focus:outline-none text-sm font-medium text-gray-900 placeholder:text-gray-400 bg-white" data-id="element-1125" />
        </div>
      </div>

      <div className="px-6 py-4 border-t border-gray-100 shrink-0 bg-gray-50 rounded-b-2xl flex flex-col sm:flex-row gap-2" data-id="element-1126">
        <Button variant="outline" onClick={handlePrint} className="flex-1 flex items-center justify-center gap-2 font-bold" data-id="element-1127">
          <Printer className="w-4 h-4" data-id="element-1128" />
          Imprimir
        </Button>
        <Button onClick={handleSendEmail} disabled={!canSend} className="flex-1 bg-gray-900 hover:bg-gray-800 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1129">
          <Mail className="w-4 h-4" data-id="element-1130" />
          Enviar por correo
        </Button>
      </div>
    </>;
}